// frontend/src/components/reputation/FullAuditModal.jsx
// Version: 2.0 (reputation dictionary i18n)
// Description: Модальное окно полного аудита репутации.
//
// 🔴 Просмотр отделён от исполнения: открытие окна не делает НИ ОДНОГО запроса,
//    живой платный прогон начинается только по нажатию кнопки запуска.
//
// 🔴 ПРАВИЛО НОЛЬ. Прежняя кнопка «Run Full Audit» крутила полосу
//    «Проверка 72 сигналов» от таймера, не сделав ни одного запроса. Здесь
//    полоса НЕ вычисляется на клиенте вообще: `progress` и состояния этапов
//    берутся из ответа сервера как есть. Единственный таймер в файле —
//    интервал опроса состояния; он спрашивает, а не рисует.
//
// 🔴 Ни одного адреса, телефона, номера Bar и ссылки на каталог: всё приходит
//    из ручки /api/v1/reputation/full-audit/*.
//
// AST-маркеры для full-audit-modal.test.js:
// NOT_CONFIGURED: API Key missing, не измеряли
// geo_mode: экспресс-прогон
// in_process_only: не переживёт перезапуск

import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useUiLanguage } from '../../context/UiLanguageContext';
import { perevesti } from '../../i18n/reputation';
import { X, Loader2, CheckCircle2, AlertTriangle, MinusCircle, ExternalLink } from 'lucide-react';

const OPROS_MS = 2000;

const ETAP_KEYS = {
  nap: 'rep.modal.audit.stage.nap',
  ahrefs: 'rep.modal.audit.stage.ahrefs',
  geo: 'rep.modal.audit.stage.geo',
  prescriptions: 'rep.modal.audit.stage.prescriptions',
};

const ISHOD = {
  PENDING: { key: 'rep.modal.audit.ishod.pending', color: 'text-[#687077] bg-[#F8F7F2] border-[#E5E1D6]', Icon: MinusCircle },
  RUNNING: { key: 'rep.modal.audit.ishod.running', color: 'text-sky-800 bg-sky-50 border-sky-200', Icon: Loader2 },
  OK: { key: 'rep.modal.audit.ishod.ok', color: 'text-emerald-700 bg-emerald-50 border-emerald-200', Icon: CheckCircle2 },
  NOT_CONFIGURED: { key: 'rep.modal.audit.ishod.not_configured', color: 'text-amber-800 bg-amber-50 border-amber-200', Icon: AlertTriangle },
  FAILED: { key: 'rep.modal.audit.ishod.failed', color: 'text-rose-700 bg-rose-50 border-rose-200', Icon: AlertTriangle },
  NOT_MEASURED: { key: 'rep.modal.audit.ishod.not_measured', color: 'text-[#687077] bg-[#F8F7F2] border-[#E5E1D6]', Icon: MinusCircle },
  not_measured: { key: 'rep.modal.audit.ishod.not_measured', color: 'text-[#687077] bg-[#F8F7F2] border-[#E5E1D6]', Icon: MinusCircle },
};

export function formatReason(t, reasonCode, params = {}) {
  if (!reasonCode) return '';
  const key = `rep.modal.audit.reason.${reasonCode}`;
  const res = t(key, params);
  if (typeof res === 'string' && res.startsWith('[rep.') && res.endsWith(']')) {
    const errClass = params?.error || reasonCode;
    return t('rep.modal.audit.reason.unexpected_error', { error: errClass });
  }
  return res;
}

// В плоской сетке показываются ТОЛЬКО отдельные значения. Объект здесь либо
// раскрывается осознанно и отдельно (как `measurement`), либо не выводится
// вовсе. Правило по РОДУ значения, а не по списку имён: список устарел бы
// молча, и поле, добавленное завтра, снова стало бы `[object Object]`.
const skalyar = (v) => v === null || v === undefined
  || typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean';

export const FullAuditModal = ({ open, onClose, tenant = 'fitenko', onCompleted }) => {
  const { lang: rawLang } = useUiLanguage();
  const lang = rawLang || 'ru';
  const t = useCallback((k, vars) => perevesti(lang, k, vars), [lang]);

  const [sostoyanie, setSostoyanie] = useState(null);
  const [oshibka, setOshibka] = useState(null);
  const [oshibkaCode, setOshibkaCode] = useState(null);
  const [zapushcheno, setZapushcheno] = useState(false);
  const taimerRef = useRef(null);

  const ostanovit = useCallback(() => {
    if (taimerRef.current) {
      clearInterval(taimerRef.current);
      taimerRef.current = null;
    }
  }, []);

  const sprosit = useCallback(async (taskId) => {
    try {
      const res = await axios.get(`/api/v1/reputation/full-audit/status/${taskId}`);
      setSostoyanie(res.data);
      if (res.data?.status === 'COMPLETED' || res.data?.status === 'FAILED') {
        ostanovit();
        if (res.data?.status === 'COMPLETED' && onCompleted) onCompleted(res.data);
      }
    } catch (err) {
      // 404 здесь означает «прогон не найден»: учёт живёт в памяти процесса и
      // не переживает перезапуск бэкенда. Это НЕ «аудит провалился».
      ostanovit();
      setZapushcheno(false);
      if (err?.response?.status === 404 || err?.response?.data?.reason_code === 'run_not_found') {
        setOshibkaCode('run_not_found');
        setOshibka(formatReason(t, 'run_not_found'));
      } else {
        const rc = err?.response?.data?.reason_code;
        setOshibka(rc
          ? formatReason(t, rc, err.response.data.params || {})
          : err?.message || t('rep.modal.audit.err_status_failed'));
      }
    }
  }, [ostanovit, onCompleted, t]);

  // 🔴 Единственное место, откуда уходит живой платный прогон. Раньше это
  // тело висело в useEffect и тратило ресурсы самим фактом открытия окна.
  const zapustit = useCallback(async () => {
    if (taimerRef.current) return; // защита от второго запуска поверх идущего
    setOshibka(null);
    setOshibkaCode(null);
    try {
      const res = await axios.post('/api/v1/reputation/full-audit/run');
      setSostoyanie(res.data);
      const taskId = res.data?.task_id;
      if (!taskId) {
        setOshibka(t('rep.modal.audit.err_no_task_id'));
        return;
      }
      taimerRef.current = setInterval(() => sprosit(taskId), OPROS_MS);
      await sprosit(taskId);
    } catch (err) {
      setZapushcheno(false);
      const rc = err?.response?.data?.reason_code;
      setOshibka(rc
        ? formatReason(t, rc, err.response.data.params || {})
        : err?.message || t('rep.modal.audit.err_not_started'));
    }
  }, [sprosit, t]);

  // Открытие окна только СБРАСЫВАЕТ показанное и убирает за собой при закрытии:
  // повторное открытие не наследует прошлый прогон и ничего не запускает.
  useEffect(() => {
    if (!open) return undefined;
    setOshibka(null);
    setOshibkaCode(null);
    setSostoyanie(null);
    setZapushcheno(false);
    return () => { ostanovit(); };
  }, [open, ostanovit]);

  if (!open) return null;

  const etapy = sostoyanie?.stages || {};
  const otchet = sostoyanie?.report;
  // 🔴 Полоса берётся с сервера. Если её нет — прочерк, а не ноль и не своё число.
  const progress = typeof sostoyanie?.progress === 'number' ? sostoyanie.progress : null;

  // Разбор словаря метрик по РОДУ значения: скаляры — в сетку, `measurement` —
  // в отдельное осознанное раскрытие, остальные вложенные поля не выводятся.
  const ahrefsPary = otchet?.ahrefs ? Object.entries(otchet.ahrefs) : [];
  const ahrefsSkalyary = ahrefsPary.filter(([, v]) => skalyar(v));
  const zamer = otchet?.ahrefs?.measurement && !skalyar(otchet.ahrefs.measurement)
    ? otchet.ahrefs.measurement : null;
  // 🔴 Чтобы вложенное поле не исчезало БЕЗЗВУЧНО, назван их СЧЁТ. Счёт — не
  // значение и имён не называет: граница показанного объявлена, а сырой ответ
  // внешнего API на экран при этом не протекает.
  const nerazvernuto = ahrefsPary.filter(([k, v]) => !skalyar(v) && k !== 'measurement').length;
  const itogoGeo = otchet?.geo?._itogo;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[88vh] overflow-y-auto shadow-xl border border-[#E5E1D6]">
        <div className="flex items-start justify-between p-5 border-b border-[#F1EEE4] sticky top-0 bg-white">
          <div>
            <h2 className="text-lg font-serif text-[#20231F]">{t('rep.modal.audit.title')}</h2>
            <p className="text-xs text-[#687077] mt-0.5">
              {t('rep.modal.audit.subtitle')}
            </p>
          </div>
          <button onClick={() => { ostanovit(); onClose(); }}
            className="p-1.5 rounded-lg hover:bg-[#F8F7F2] text-[#687077] cursor-pointer" aria-label={t('rep.modal.audit.close')}>
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {oshibka && (
            <div className="border border-rose-200 bg-rose-50 rounded-xl p-4 text-xs text-rose-800 space-y-2">
              <div>{oshibka}</div>
              {oshibkaCode === 'run_not_found' && (
                <button
                  type="button"
                  onClick={() => { setZapushcheno(true); zapustit(); }}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#20231F] text-white hover:opacity-90 cursor-pointer"
                >
                  {t('rep.modal.audit.btn_run_again')}
                </button>
              )}
            </div>
          )}

          <div className="border border-[#E5E1D6] bg-[#FCFBF8] rounded-xl p-4">
            <button
              type="button"
              onClick={() => { setZapushcheno(true); zapustit(); }}
              disabled={zapushcheno}
              className="px-4 py-2 rounded-lg text-sm font-semibold bg-[#20231F] text-white disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              {zapushcheno ? t('rep.modal.audit.btn_running') : t('rep.modal.audit.btn_run')}
            </button>
            {/* 🔴 Состав прогона назван СЛОВАМИ. Ни числа вызовов, ни units:
                константы «тридцать вызовов» в коде нет, число подсказок и
                повторов берётся из конфига арендатора, а расход units мы не
                измеряли. Пустота видна, выдумка — нет. */}
            <p className="text-[11px] text-[#687077] mt-2 leading-snug">
              {t('rep.modal.audit.disclaimer')}
            </p>
          </div>

          <div>
            <div className="flex items-center justify-between text-xs text-[#687077] mb-1.5">
              <span>{t('rep.modal.audit.progress')}</span>
              <span className="font-mono">{progress === null ? '—' : `${progress}%`}</span>
            </div>
            <div className="h-2 bg-[#F1EEE4] rounded-full overflow-hidden">
              <div className="h-full bg-[#20231F] transition-all duration-300"
                   style={{ width: `${progress === null ? 0 : progress}%` }} />
            </div>
          </div>

          <ul className="space-y-2">
            {Object.keys(ETAP_KEYS).map((kluch) => {
              const st = etapy[kluch]?.status || 'PENDING';
              const meta = ISHOD[st] || ISHOD.PENDING;
              const Icon = meta.Icon;
              const reasonCode = etapy[kluch]?.reason_code;
              return (
                <li key={kluch} className="flex items-start gap-3">
                  <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-medium border shrink-0 ${meta.color}`}>
                    <Icon className={`w-3 h-3 ${st === 'RUNNING' ? 'animate-spin' : ''}`} />
                    {t(meta.key)}
                  </span>
                  <div className="text-xs text-[#20231F] leading-tight">
                    {t(ETAP_KEYS[kluch])}
                    {reasonCode && (
                      <div className="text-[10px] text-[#687077] mt-0.5">
                        {formatReason(t, reasonCode, etapy[kluch]?.params || {})}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>

          {otchet && (
            <div className="space-y-4 border-t border-[#F1EEE4] pt-4">
              {otchet.nap && (
                <div className="bg-[#FCFBF8] border border-[#E5E1D6] rounded-xl p-4">
                  <div className="text-xs text-[#687077] uppercase tracking-wider">{t('rep.modal.audit.directories')}</div>
                  <div className="text-sm text-[#20231F] mt-1">
                    {otchet.nap.measured_count == null ? (
                      t('rep.modal.audit.nap_not_measured')
                    ) : (
                      t('rep.modal.audit.nap_summary', {
                        measured: otchet.nap.measured_count,
                        total: otchet.nap.total_count,
                        index: otchet.nap.overall_score === null || otchet.nap.overall_score === undefined ? '—' : `${otchet.nap.overall_score}%`,
                      })
                    )}
                  </div>
                  {otchet.nap.measured_method === 'human_screenshot' && (
                    <div className="text-[10px] text-[#687077] mt-1">
                      {t('rep.modal.audit.nap_method', { date: otchet.nap.measured_at })}
                    </div>
                  )}
                </div>
              )}

              <div className="bg-[#FCFBF8] border border-[#E5E1D6] rounded-xl p-4">
                <div className="text-xs text-[#687077] uppercase tracking-wider">{t('rep.modal.audit.domain_metrics')}</div>
                {etapy.ahrefs?.status === 'NOT_CONFIGURED' ? (
                  // 🔴 Здесь запрещены нули и пустые графики: «ключа нет» —
                  // это утверждение о НАС, а ноль был бы утверждением о домене.
                  <div className="text-xs text-amber-800 mt-1">
                    {etapy.ahrefs?.reason_code
                      ? formatReason(t, etapy.ahrefs.reason_code, etapy.ahrefs.params || {})
                      : formatReason(t, 'missing_key', { key: 'AHREFS_API_KEY' })}
                  </div>
                ) : otchet.ahrefs ? (
                  <>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-2">
                      {ahrefsSkalyary.map(([k, v]) => (
                        <div key={k}>
                          <div className="text-[10px] text-[#687077] uppercase">{k}</div>
                          <div className="text-sm font-mono text-[#20231F]">
                            {v === null || v === undefined ? '—' : String(v)}
                          </div>
                        </div>
                      ))}
                    </div>

                    {nerazvernuto > 0 && (
                      <div className="text-[10px] text-[#687077] mt-2">
                        {t('rep.modal.audit.hidden_nested', { count: nerazvernuto })}
                      </div>
                    )}

                    {/* 🔴 Метаданные замера заведены ради того, чтобы числа не
                        сравнивали вслепую. Значения показываются так, КАК
                        ПРИШЛИ: второй способ отображения — второй держатель, он
                        разойдётся молча. Отсутствующее поле — не строка с
                        прочерком-заглушкой, а отсутствие строки. */}
                    {zamer && (
                      <div data-blok="zamer"
                           className="mt-3 pt-2 border-t border-[#E5E1D6] text-[11px] text-[#687077] space-y-0.5">
                        {(zamer.source || zamer.target) && (
                          <div>
                            {zamer.source && <span>{t('rep.modal.audit.measured_by', { source: zamer.source })}</span>}
                            {zamer.source && zamer.target && <span> · </span>}
                            {zamer.target && <span>{t('rep.modal.audit.target_domain', { target: zamer.target })}</span>}
                          </div>
                        )}
                        {zamer.measured_at && <div>{t('rep.modal.audit.measured_at', { date: zamer.measured_at })}</div>}
                        {zamer.snapshot_date && (
                          <div>
                            {t('rep.modal.audit.snapshot_for', { date: zamer.snapshot_date })}
                            {zamer.snapshot_clock && <span> · {t('rep.modal.audit.clock')}: {zamer.snapshot_clock}</span>}
                            {zamer.snapshot_tz && <span> ({zamer.snapshot_tz})</span>}
                          </div>
                        )}
                        {zamer.country_filter_applied === false && (
                          <div>{t('rep.modal.audit.no_country_filter')}</div>
                        )}
                        {zamer.country_filter_applied === true && (
                          <div>{t('rep.modal.audit.country_filter', { country: zamer.country ?? '—' })}</div>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-xs text-[#687077] mt-1">
                    {etapy.ahrefs?.reason_code
                      ? formatReason(t, etapy.ahrefs.reason_code, etapy.ahrefs.params || {})
                      : t('rep.modal.audit.no_values')}
                  </div>
                )}
              </div>

              {otchet.geo && (
                <div className="bg-[#FCFBF8] border border-[#E5E1D6] rounded-xl p-4">
                  <div className="text-xs text-[#687077] uppercase tracking-wider">{t('rep.modal.audit.neural_networks')}</div>
                  {otchet.geo_mode === 'express' && (
                    <div className="text-[10px] text-amber-800 mt-1">
                      {t('rep.modal.audit.express_mode')}
                    </div>
                  )}
                  <div className="mt-2 space-y-1">
                    {Object.entries(otchet.geo)
                      .filter(([k]) => !k.startsWith('_'))
                      .map(([provider, v]) => (
                        <div key={provider} data-provider={provider}
                             className="flex items-start gap-2 text-xs">
                          <span className="font-medium text-[#20231F] w-24 shrink-0">{provider}</span>
                          {/* 🔴 `?? '—'` — именно `??`, не `||`: честный ноль
                              провайдера обязан печататься нулём. У веток «не
                              спрашивали» и «отказ» чисел нет ВОВСЕ: «нам не с
                              чем сравнивать» и «ноль» — разные утверждения. */}
                          <span className="text-[#687077]">
                            {v.status === 'NOT_CONFIGURED'
                              ? t('rep.modal.audit.geo_not_queried', {
                                  detail: v.reason_code
                                    ? formatReason(t, v.reason_code, v.params || {})
                                    : '—',
                                })
                              : v.status === 'OK'
                                ? t('rep.modal.audit.geo_stats', {
                                    done: v.prompts_done ?? '—',
                                    our: v.our_citations ?? '—',
                                    total: v.total_citations ?? '—',
                                    unknown: v.citations_domain_unknown ?? '—',
                                  })
                                : t('rep.modal.audit.geo_failed', {
                                    detail: v.reason_code
                                      ? formatReason(t, v.reason_code, v.params || {})
                                      : '—',
                                  })}
                          </span>
                        </div>
                      ))}
                  </div>

                  {/* 🔴 Число опрошенных ответов здесь НЕ складывается: свой
                      счёт стал бы вторым держателем и разошёлся бы с бэкендом
                      молча. Показано ровно то, что положил бэкенд. */}
                  {itogoGeo ? (
                    <div data-blok="geo-itogo"
                         className="mt-2 pt-2 border-t border-[#E5E1D6] text-xs text-[#20231F]">
                      {t('rep.modal.audit.geo_total', {
                        our: itogoGeo.our_citations_total ?? '—',
                        total: itogoGeo.citations_total ?? '—',
                        unknown: itogoGeo.citations_domain_unknown_total ?? '—',
                      })}
                      <div className="text-[10px] text-[#687077] mt-0.5">
                        {t('rep.modal.audit.geo_total_desc')}
                      </div>
                    </div>
                  ) : (
                    // Свода нет СОЗНАТЕЛЬНО, когда не ответил никто: ноль
                    // читался бы как «ссылок не нашлось».
                    <div className="mt-2 pt-2 border-t border-[#E5E1D6] text-[11px] text-[#687077]">
                      {t('rep.modal.audit.geo_no_summary')}
                    </div>
                  )}
                </div>
              )}

              {Array.isArray(otchet.prescriptions) && otchet.prescriptions.length > 0 && (
                <div className="bg-white border border-[#E5E1D6] rounded-xl p-4">
                  <div className="text-xs text-[#687077] uppercase tracking-wider">
                    {t('rep.modal.audit.what_to_fix', { count: otchet.prescriptions.length })}
                  </div>
                  <ul className="mt-2 space-y-2">
                    {otchet.prescriptions.map((p, i) => (
                      <li key={`${p.directory_key}-${i}`} className="flex items-start justify-between gap-3">
                        <div className="text-xs">
                          <span className={p.severity === 'critical' ? 'text-rose-800 font-medium' : 'text-[#20231F]'}>
                            {p.directory_name}
                          </span>
                          <span className="text-[#687077]"> — {p.issue}</span>
                        </div>
                        {p.profile_url && (
                          <a href={p.profile_url} target="_blank" rel="noopener noreferrer"
                             className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#20231F] hover:underline shrink-0">
                            {t('rep.modal.audit.open_link')} <ExternalLink className="w-3 h-3 text-[#C8A653]" />
                          </a>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {sostoyanie?.storage === 'in_process_only' && (
                <div className="text-[10px] text-[#687077]">
                  {t('rep.modal.audit.storage_disclaimer')}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FullAuditModal;
